import type { TFunction } from 'react-i18next';

import { EFuelGrade, EFuelType } from '@/config/station';
import { dateToDoubleDecker } from '@/utils/dateUtils';
import { formatAddress } from '@/utils/stringUtils';

import type {
  TGetTransactionsResponse,
  TTransactionAccountDetails,
  TTransactionListItem,
} from '@/types/transaction';

/**
 * Text helpers and the row view-model for the two "EV Transactions List" tables:
 *  - side menu page `src/pages/Admin/Transactions`
 *  - site page, Transactions tab, EV Transactions view: `src/pages/Admin/StationEntity/Transactions`
 *
 * Both lists read the same `/customer/transactions` response (`TTransaction`) and show the same
 * five columns (`EV_TRANSACTION_LIST_COLUMNS`), so the cell text is built once here. A fix here
 * reaches both tables.
 *
 * Other pages (add pump, fuel details, customers, payments, members) also import some of these
 * helpers through `src/pages/Admin/Transactions/ListView/utils.ts`, which re-exports them.
 */

/**
 * Translation key for a fuel type.
 * Returns `undefined` for a type with no key. Callers pass the result straight to `t()`.
 */
export const typeToDisplayKey = (type: EFuelType) => {
  switch (type) {
    case EFuelType.GASOLINE:
      return 'station.fuelType.gasoline';

    case EFuelType.DIESEL:
      return 'station.fuelType.diesel';

    case EFuelType.ELECTRIC:
      return 'station.fuelType.electric';
  }
};

/**
 * Translation key for a fuel grade.
 * Returns `undefined` for a grade with no key (the row view-model then falls back to a fixed
 * text, see `extractBaseTransactionVM`).
 */
export const gradeToDisplayKey = (grade: EFuelGrade) => {
  switch (grade) {
    case EFuelGrade.ECONOMY:
      return 'station.fuelGrade.economy';

    case EFuelGrade.REGULAR:
      return 'station.fuelGrade.regular';

    case EFuelGrade.MID_GRADE:
      return 'station.fuelGrade.midGrade';

    case EFuelGrade.PREMIUM:
      return 'station.fuelGrade.premium';

    case EFuelGrade.OCTANE_93:
      return 'station.fuelGrade.93';

    case EFuelGrade.DIESEL:
      return 'station.fuelGrade.diesel';

    case EFuelGrade.FAST_CHARGE:
      return 'station.fuelGrade.fastCharge';

    case EFuelGrade.STD_CHARGE:
      return 'station.fuelGrade.stdCharge';
  }
};

/**
 * "Price | Discount" cell text from the first detail line of a transaction.
 *
 * - No details: `{ title: '' }`, the cell shows a dash.
 * - Detail `itemType` `'ELECTRIC'`: price per kWh. Any other item type: price per gallon.
 */
export const extractTransactionCost = (
  details: TTransactionAccountDetails | undefined,
  t: TFunction,
) => {
  if (!details) {
    return { title: '' };
  }
  if (details.itemType === 'ELECTRIC') {
    return {
      title: t('transactions.list.pricePerKwh', { value: details.price }),
      details: t('transactions.list.pricePerKwh', { value: details.discountAmount }),
    };
  }
  return {
    title: t('transactions.list.pricePerGallon', { value: details.price }),
    details: t('transactions.list.pricePerGallon', { value: details.discountAmount }),
  };
};

/**
 * Station address text. The backend sends either plain text or a JSON string of an address
 * object (starts with `{`). A JSON string is formatted as `line1, city, state zip`.
 * Plain text, an empty string and `undefined` pass through unchanged.
 */
export const extractAddress = (address?: string) => {
  if (address?.startsWith('{')) {
    return formatAddress(JSON.parse(address));
  }
  return address;
};

/**
 * "Total Dispensed" cell text from the first detail line: kWh text for `'ELECTRIC'`, gallon text
 * for any other item type. No details: empty text, the cell shows a dash.
 */
export const extractTotalDispensed = (
  details: TTransactionAccountDetails | undefined,
  t: TFunction,
) => {
  if (!details) {
    return { quantity: '' };
  }

  if (details.itemType === 'ELECTRIC') {
    return {
      quantity: t('transactions.list.dispensedPricePerKwh', { value: details.quantity }),
    };
  }
  return {
    quantity: t('transactions.list.dispensedPricePerGallon', { value: details.quantity }),
  };
};

/**
 * Row view-model of both EV Transactions lists (each list passes it to `useCollection`).
 * Fields and their source in `TTransaction`:
 * - `logo` + `station`: "Site Name | Location" = `stationLogoUrl` (thumbnail), `stationName`,
 *   and `address` (plain text or a JSON address string, see `extractAddress`).
 * - `date`: "Date | Time" from `date`.
 * - `cost`: "Price | Discount" = `price` and `discountAmount` of the first
 *   `customerTransactionDetails` line.
 * - `totalDispensed`: "Total Dispensed" = `quantity` of that same line (kWh or gallon).
 * - `amount`: "Transaction Amount" = `transactionAmount` (not `totalAmount`).
 * - `fuel`: no column shows it today.
 *
 * Do not add a field here unless both lists show it the same way.
 *
 * Edge cases kept from the original lists: the time is the browser clock with no zone label;
 * a missing grade key falls back to the text `'200 kWh'`; `logo.brand` is always empty.
 */
export const extractBaseTransactionVM = (transaction: TTransactionListItem, t: TFunction) => ({
  id: transaction.id,
  entity: transaction,

  date: dateToDoubleDecker(transaction.date, { includeTimezone: false }),
  cost: extractTransactionCost(transaction.customerTransactionDetails?.[0], t),
  amount: t('format.currency', { value: transaction.transactionAmount }),
  totalDispensed: extractTotalDispensed(transaction.customerTransactionDetails?.[0], t),
  logo: {
    logo: transaction.stationLogoUrl,
    brand: '',
  },
  station: {
    title: transaction.stationName,
    details: extractAddress(transaction.address),
  },
  fuel: {
    title: t(typeToDisplayKey(transaction.fuelType)),
    details: t(gradeToDisplayKey(transaction.fuelGrade) || '200 kWh'),
  },
});

/**
 * Totals data from the response: `total` is the amount over all matching rows (not only
 * the shown page) and `totalHits` is the matching row count.
 *
 * Must stay a module-level function: `useCollection` lists it as an effect dependency, so a
 * new function on each render would refetch the list in a loop.
 */
export const extractTransactionSummary = (data: TGetTransactionsResponse) => ({
  totalAmount: data.data.total,
  transactionsCount: data.data.totalHits,
});
